// src/pages/ReportsPage.jsx
import { useEffect, useState } from 'react';
import {
  SimpleGrid,
  Paper,
  Text,
  Group,
  ThemeIcon,
  Title,
  Stack,
  LoadingOverlay,
  Box,
  Center,
  Button,
  Badge,
} from '@mantine/core';
import {
  IconChartPie,
  IconTrendingUp,
  IconCoin,
  IconAlertCircle,
  IconDownload,
  IconCar,
  IconArchive,
  IconTool,
  IconShieldCheck,
  IconFileCertificate,
} from '@tabler/icons-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import api from '../../api/axiosConfig';
import { notifications } from '@mantine/notifications';
import { downloadVehicleExcel, VEHICLE_EXCEL_REPORTS } from './utils/vehicleExcelDownload';

// Pasta Grafik Renkleri
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

export default function ReportsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingKey, setExportingKey] = useState(null);

  const reportCards = [
    { key: 'active', icon: IconCar, color: 'blue', detail: 'Aktif filo ve belge bitiş tarihleri' },
    { key: 'archive', icon: IconArchive, color: 'gray', detail: 'Satılan, hurdaya ayrılan ve pasif araçlar' },
    { key: 'maintenance', icon: IconTool, color: 'teal', detail: 'Bakım planı, bakım geçmişi ve maliyetler' },
    { key: 'inspection', icon: IconAlertCircle, color: 'red', detail: 'Muayenesi geçmiş veya 30 gün içinde dolacak araçlar' },
    { key: 'insurance', icon: IconFileCertificate, color: 'orange', detail: 'Trafik sigortası uyarıları (30 gün)' },
    { key: 'casco', icon: IconShieldCheck, color: 'grape', detail: 'Kasko bitiş uyarıları (30 gün)' },
  ];

  const handleReportExport = async (key) => {
    if (exportingKey) return;
    setExportingKey(key);
    try {
      await downloadVehicleExcel(key);
      notifications.show({ title: 'Excel indiriliyor', message: `${VEHICLE_EXCEL_REPORTS[key].title} - Tasarım V2`, color: 'teal' });
    } catch (error) {
      notifications.show({ title: 'Excel indirilemedi', message: error.message, color: 'red' });
    } finally {
      setExportingKey(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/vehicles/reports/summary');
        setData(response.data);
      } catch (error) {
        console.error('Rapor verisi çekilemedi', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (val) => {
    const numericValue = Number(val || 0);
    return numericValue.toLocaleString('tr-TR', {
      style: 'currency',
      currency: 'TRY',
    });
  };

  // Excel Rapor Merkezi finansal özet API'si hata verse bile kullanılabilir.
  const topVehicles = Array.isArray(data?.top_vehicles) ? data.top_vehicles : [];
  const categoryDistribution = Array.isArray(data?.category_distribution)
    ? data.category_distribution
    : [];
  const monthlyTrend = Array.isArray(data?.monthly_trend) ? data.monthly_trend : [];

  return (
    <Stack gap="lg">
      <Group>
        <IconChartPie size={28} color="#228be6" />
        <Title order={2}>Finansal Raporlar ve Analizler</Title>
      </Group>

      {/* KURUMSAL EXCEL RAPOR MERKEZİ - TASARIM V2 */}
      <Paper
        p={{ base: 'md', md: 'xl' }}
        radius="xl"
        withBorder
        style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)' }}
      >
        <Stack gap="lg">
          <Group justify="space-between" gap="sm" wrap="wrap">
            <div>
              <Title order={3}>Excel Rapor Merkezi</Title>
              <Text size="sm" c="dimmed">Altı kurumsal filo raporunu yönetici özeti ve düzenli tablolarla indirin.</Text>
            </div>
            <Badge size="lg" color="teal" variant="light" radius="xl">Tasarım V2</Badge>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2, xl: 3 }} spacing="md">
            {reportCards.map(({ key, icon: Icon, color, detail }) => (
              <Paper key={key} withBorder p="md" radius="lg" bg="white">
                <Stack gap="sm" justify="space-between" h="100%">
                  <Group gap="sm" wrap="nowrap" align="flex-start">
                    <ThemeIcon size={42} radius="lg" color={color} variant="light">
                      <Icon size={22} />
                    </ThemeIcon>
                    <div>
                      <Text size="sm" fw={800}>{VEHICLE_EXCEL_REPORTS[key].title}</Text>
                      <Text size="xs" c="dimmed">{detail}</Text>
                    </div>
                  </Group>
                  <Button
                    fullWidth
                    variant="light"
                    color={color}
                    radius="md"
                    leftSection={<IconDownload size={16} />}
                    loading={exportingKey === key}
                    disabled={exportingKey !== null && exportingKey !== key}
                    onClick={() => handleReportExport(key)}
                  >
                    Excel İndir
                  </Button>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Stack>
      </Paper>

      {loading ? (
        <Box pos="relative" mih={220}><LoadingOverlay visible /></Box>
      ) : !data ? (
        <Center h={140}><Text c="dimmed">Finansal özet yüklenemedi. Excel raporlarını yine de indirebilirsiniz.</Text></Center>
      ) : (
      <>
      {/* 1. ÖZET KARTLAR */}
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Paper withBorder p="md" radius="md" shadow="sm">
          <Group justify="space-between">
            <Text size="xs" c="dimmed" fw={700} tt="uppercase">
              TOPLAM FİLO GİDERİ
            </Text>
            <ThemeIcon color="red" variant="light" size="lg">
              <IconCoin size={20} />
            </ThemeIcon>
          </Group>
          <Text fw={700} size="xl" mt="sm">
            {formatCurrency(data.grand_total)}
          </Text>
          <Text c="dimmed" size="xs" mt={5}>
          </Text>
        </Paper>

        <Paper withBorder p="md" radius="md" shadow="sm">
          <Group justify="space-between">
            <Text size="xs" c="dimmed" fw={700} tt="uppercase">
              EN MALİYETLİ ARAÇ
            </Text>
            <ThemeIcon color="orange" variant="light" size="lg">
              <IconAlertCircle size={20} />
            </ThemeIcon>
          </Group>
          <Text fw={700} size="xl" mt="sm">
            {topVehicles[0]?.name || '-'}
          </Text>
          <Text c="dimmed" size="xs" mt={5}>
            {topVehicles[0] ? formatCurrency(topVehicles[0].value) : '₺0,00'}
          </Text>
        </Paper>

        <Paper withBorder p="md" radius="md" shadow="sm">
          <Group justify="space-between">
            <Text size="xs" c="dimmed" fw={700} tt="uppercase">
              HARCAMA KALEMİ SAYISI
            </Text>
            <ThemeIcon color="blue" variant="light" size="lg">
              <IconTrendingUp size={20} />
            </ThemeIcon>
          </Group>
          <Text fw={700} size="xl" mt="sm">
            {categoryDistribution.length}
          </Text>
          <Text c="dimmed" size="xs" mt={5}>
            Farklı Gider Türü
          </Text>
        </Paper>
      </SimpleGrid>

      {/* 2. İLK İKİ GRAFİK */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        {/* A. ARAÇ BAZLI HARCAMA (BAR CHART) */}
        <Paper withBorder p="md" radius="md" shadow="sm">
          <Text fw={600} mb="md">
            Araç Bazlı Toplam Harcamalar (Top 5)
          </Text>

          <Box style={{ width: '100%', height: 320, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topVehicles}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar
                  dataKey="value"
                  name="Tutar"
                  fill="#228be6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        {/* B. GİDER DAĞILIMI (PIE CHART) */}
        <Paper withBorder p="md" radius="md" shadow="sm">
          <Text fw={600} mb="md">
            Gider Türü Dağılımı
          </Text>

          <Box style={{ width: '100%', height: 320, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name} ${((percent || 0) * 100).toFixed(0)}%`
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryDistribution.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </SimpleGrid>

      {/* 3. AYLIK TREND */}
      <Paper withBorder p="md" radius="md" shadow="sm">
        <Text fw={600} mb="md">
          Bu Yılın Aylık Harcama Trendi
        </Text>

        <Box style={{ width: '100%', height: 320, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={monthlyTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Line
                type="monotone"
                dataKey="expense"
                name="Aylık Gider"
                stroke="#82ca9d"
                strokeWidth={3}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
      </>
      )}
    </Stack>
  );
}